import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { DataTableColumn, DataTableSortDirection } from 'src/app/shared/ui/data-table/data-table.component';
import { SellerService, FileService, LocalService, Errors, ProfileService, formatDate } from '../../../../core';
import { TranslateService } from '@ngx-translate/core';
import { sellerApi } from '../../../../config';
import { ActivityFilterValue } from '../activity-filter-form/activity-filter-form.component';

@Component({
  selector: 'app-user-activity',
  templateUrl: './user-activity.component.html',
  styleUrls: ['./user-activity.component.scss']
})
export class UserActivityComponent implements OnInit, AfterViewInit {
  @ViewChild('pointSummaryDateTemplate') pointSummaryDateTemplate!: TemplateRef<any>;
  @ViewChild('pointsEarnedTemplate') pointsEarnedTemplate!: TemplateRef<any>;
  @ViewChild('currentBalanceTemplate') currentBalanceTemplate!: TemplateRef<any>;
  @ViewChild('pointsRedeemedTemplate') pointsRedeemedTemplate!: TemplateRef<any>;

  userProfile: any;
  usersList: any[] = [];
  minDate: Date = new Date(2020, 7, 31);
  maxDate: Date = new Date();
  userPointSummaryData: any;
  userPointSummaryPage = 0;
  userPointSummarySortBy = '';
  userPointSummarySortDir: DataTableSortDirection = '';
  userPointSummaryLoading = false;
  userPointSummaryColumns: DataTableColumn[] = [];
  errors: Errors = { errors: {} };
  pageSize = 5;

  private lastFilter: ActivityFilterValue = { selectedUsers: [], startDate: null, endDate: null };

  constructor(
    private sellerService: SellerService,
    private fileService: FileService,
    private profileService: ProfileService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.userProfile = LocalService.get('userProfile');
    this.getUsersUnderManager();
  }

  ngAfterViewInit(): void {
    this.userPointSummaryColumns = [
      { field: 'date', header: this.translate.instant('accountDetails.dateColumn'), align: 'left', sortable: true, cellTemplate: this.pointSummaryDateTemplate },
      { field: 'pointsEarned', header: this.translate.instant('accountDetails.pointsEarned'), align: 'right', cellTemplate: this.pointsEarnedTemplate },
      { field: 'currentBalance', header: this.translate.instant('accountDetails.currentBalance'), align: 'right', cellTemplate: this.currentBalanceTemplate },
      { field: 'pointsRedeemed', header: this.translate.instant('accountDetails.pointsRedeemed'), align: 'right', cellTemplate: this.pointsRedeemedTemplate },
      { field: 'status', header: this.translate.instant('accountDetails.status'), align: 'left' }
    ];
  }

  onFilterSearch(filter: ActivityFilterValue): void {
    this.lastFilter = filter;
    this.userPointSummaryPage = 0;
    this.userPointSummarySortBy = '';
    this.userPointSummarySortDir = '';
    this.fetchUserPointSummaryData(0);
  }

  onFilterReset(): void {
    this.lastFilter = { selectedUsers: [], startDate: null, endDate: null };
    this.userPointSummaryPage = 0;
    this.userPointSummarySortBy = '';
    this.userPointSummarySortDir = '';
    this.userPointSummaryData = null;
  }

  onFilterExport(filter: ActivityFilterValue): void {
    this.lastFilter = filter;
    const url = this.buildExportUrl();
    const downloadName = this.translate.instant('accountDetails.downloadPointSummary');
    const fileName = downloadName + '_' + this.userProfile.company.id + '.xlsx';
    this.fileService.excelDownloadFile(url, fileName);
  }

  onUserPointSummaryPageChange(newPage: number): void {
    this.userPointSummaryPage = newPage - 1;
    this.fetchUserPointSummaryData(newPage - 1);
  }

  onUserPointSummarySort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.userPointSummarySortBy = event.sortBy;
    this.userPointSummarySortDir = event.sortDir;
    this.userPointSummaryPage = 0;
    this.fetchUserPointSummaryData(0);
  }

  splitData(value: any): string {
    return parseFloat(value).toLocaleString();
  }

  getUsersUnderManager(): void {
    this.profileService.companyAllUsers(this.userProfile.company.id).subscribe(
      (data: any[]) => {
        if (data) {
          this.usersList = data.reduce((users: any[], item: any) => {
            if (users.find((obj: any) => obj.id === item.id) === undefined) {
              users.push({ id: item.id, fullName: item.firstName + ' ' + item.lastName });
            }
            return users;
          }, []).sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
        }
      },
      (err: any) => {
        this.errors = err;
      }
    );
  }

  private fetchUserPointSummaryData(pageIndex: number = 0): void {
    const startDate = formatDate(this.lastFilter.startDate, 'YYYY-MM-DD HH:mm:ss');
    const endDate = formatDate(this.lastFilter.endDate, 'YYYY-MM-DD HH:mm:ss');

    const params: any = {
      page: pageIndex,
      size: this.pageSize,
      userIds: this.lastFilter.selectedUsers.map((x: any) => x.id).join(','),
      startDate: startDate,
      endDate: endDate
    };

    if (this.userPointSummarySortBy) {
      params.sortBy = this.userPointSummarySortBy;
      params.sortDir = this.userPointSummarySortDir;
    }

    this.userPointSummaryLoading = true;
    this.sellerService.getPointTransaction(params).subscribe(
      (data: any) => {
        this.userPointSummaryData = data;
        this.userPointSummaryLoading = false;
      },
      (err: any) => {
        this.errors = err;
        this.userPointSummaryLoading = false;
      }
    );
  }

  private buildExportUrl(): string {
    const startDate = formatDate(this.lastFilter.startDate, 'YYYY-MM-DD HH:mm:ss');
    const endDate = formatDate(this.lastFilter.endDate, 'YYYY-MM-DD HH:mm:ss');
    const userIds = this.lastFilter.selectedUsers.map((x: any) => x.id).join(',');
    return sellerApi.pointTransactionHistory +
      '?userIds=' + userIds +
      '&startDate=' + startDate +
      '&endDate=' + endDate +
      '&excel=true';
  }
}
